import { getRoot, getSelector } from './dom';

/**
 * Рекламные блоки. Своей вёрстки у нас для них нет: слоты создаёт adfox
 * (`<div id="adfox_a1c3i6">`), а обёртки приезжают внутри чужих микрофронтендов
 * (`data-name="AdfoxCarouselBannerWrapper"`, `data-name="BrandingBanner"`).
 * Классы хешируются, но исходное имя остаётся подстрокой.
 */
const BANNER_SELECTOR =
  '[id*="adfox" i],[class*="adfox" i],[data-name*="Adfox" i],[class*="banner" i],[id*="banner" i],[class*="advert" i],[data-name*="Banner" i],[data-name*="Advert" i]';

/**
 * Называется баннером, но рекламой не является: наш же промоблок «скачайте
 * приложение» в мобильном футере. Приезжает уже в разметке футера, ничего не
 * догружает — засчитывать его в рекламу значит соврать о причине сдвига.
 */
const NOT_BANNER_SELECTOR = '[data-name="DownloadAppBanner"]';

/** Ниже этого размера блок не баннер, а иконка или бейдж с похожим именем класса. */
const MIN_BANNER_SIDE = 40;

export interface IBannerInfo {
  /** Сколько рекламных блоков на странице к моменту отправки отчёта. */
  bannerCount: number;
  /**
   * Есть ли баннер выше корня приложения. Такой блок при загрузке толкает
   * вниз всю страницу, но в источниках сдвига будет числиться корень, а не он —
   * без этого флага причина выглядела бы как `outside`.
   */
  bannerAbove: boolean;
  /** Суммарная высота рекламных блоков: сравнить со сдвигом, если он крупный. */
  bannerHeight: number;
  /**
   * Чем опознались найденные блоки. Детектор ищет по подстроке в имени, поэтому
   * обязан показывать, что именно поймал: иначе промоблок, случайно названный
   * баннером, молча превратится в «виновата реклама».
   */
  bannerNames: string[];
}

/** Ближайший рекламный предок, с пропуском блоков из списка исключений. */
export function findBannerAncestor(element: Element): Element | null {
  let current = element.closest(BANNER_SELECTOR);

  while (current?.matches(NOT_BANNER_SELECTOR)) {
    current = current.parentElement?.closest(BANNER_SELECTOR) ?? null;
  }

  return current;
}

/** Все рекламные блоки на странице, кроме исключений, пустых и схлопнутых. */
function findBanners(): Element[] {
  return Array.from(document.querySelectorAll(BANNER_SELECTOR)).filter(element => {
    if (element.matches(NOT_BANNER_SELECTOR)) {
      return false;
    }

    const { width, height } = element.getBoundingClientRect();

    return width >= MIN_BANNER_SIDE && height >= MIN_BANNER_SIDE;
  });
}

/**
 * Что на странице с рекламой. Нужно и тем сессиям, где сдвига в самом баннере не было:
 * пустой отчёт о сдвигах при наличии баннера — это тоже ответ.
 */
export function getBannerInfo(rootId: string): IBannerInfo {
  const banners = findBanners();
  const rootTop = getRoot(rootId)?.getBoundingClientRect().top ?? null;

  return {
    bannerCount: banners.length,
    bannerAbove: rootTop !== null && banners.some(element => element.getBoundingClientRect().bottom <= rootTop),
    bannerHeight: Math.round(banners.reduce((sum, element) => sum + element.getBoundingClientRect().height, 0)),
    bannerNames: banners.slice(0, 5).map(element => getSelector(element)),
  };
}
